import * as XLSX from 'xlsx';

/**
 * Generate filename for export
 * Format: AZ_Takeoff_<Category>_<YYYY-MM-DD>
 */
export const generateFilename = (category, extension) => {
  const date = new Date().toISOString().split('T')[0];
  const cleanCategory = category.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
  return `AZ_Takeoff_${cleanCategory}_${date}.${extension}`;
};

/**
 * Export takeoff data to CSV
 */
export const exportToCSV = (takeoff, modelName = 'BIM Model') => {
  const lines = [];
  
  // Header
  lines.push('Anton Zolotarov BIM AI Platform - Material Takeoff');
  lines.push('');
  lines.push(`Model,${modelName}`);
  lines.push(`Category,${takeoff.category}`);
  lines.push(`Quantity Type,${takeoff.quantityType}`);
  lines.push(`Total,${takeoff.total?.toLocaleString() || 'N/A'} ${takeoff.unit}`);
  lines.push(`Elements with Data,${takeoff.elementsWithData} of ${takeoff.elementCount}`);
  lines.push(`Generated,${new Date(takeoff.timestamp).toLocaleString()}`);
  lines.push('');
  
  // Per-level breakdown
  if (takeoff.byLevel && takeoff.byLevel.length > 0) {
    lines.push('Level Breakdown');
    lines.push(`Level,Value (${takeoff.unit}),Element Count`);
    takeoff.byLevel.forEach(level => {
      lines.push(`${level.level},${level.value?.toLocaleString() || 0},${level.count}`);
    });
  }
  
  const csvContent = lines.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const filename = generateFilename(takeoff.category, 'csv');
  
  downloadBlob(blob, filename);
};

/**
 * Export takeoff data to Excel with minimal branding
 */
export const exportToExcel = (takeoff, modelName = 'BIM Model') => {
  const wb = XLSX.utils.book_new();
  
  // Create data array for the sheet
  const data = [
    ['Anton Zolotarov BIM AI Platform - Material Takeoff'],
    [],
    ['Model', modelName],
    ['Category', takeoff.category],
    ['Quantity Type', takeoff.quantityType],
    ['Total', `${takeoff.total?.toLocaleString() || 'N/A'} ${takeoff.unit}`],
    ['Elements with Data', `${takeoff.elementsWithData} of ${takeoff.elementCount}`],
    ['Generated', new Date(takeoff.timestamp).toLocaleString()],
    [],
  ];
  
  // Add per-level breakdown if available
  if (takeoff.byLevel && takeoff.byLevel.length > 0) {
    data.push(['Level Breakdown']);
    data.push(['Level', `Value (${takeoff.unit})`, 'Element Count']);
    takeoff.byLevel.forEach(level => {
      data.push([level.level, level.value || 0, level.count]);
    });
  }
  
  const ws = XLSX.utils.aoa_to_sheet(data);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 30 },  // Column A
    { wch: 20 },  // Column B
    { wch: 15 },  // Column C
  ];
  
  // Apply minimal styling (bold for title and headers)
  // Note: xlsx library has limited styling support without xlsx-style
  // We'll use cell merging for the title
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }, // Merge title row
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Takeoff');
  
  const filename = generateFilename(takeoff.category, 'xlsx');
  XLSX.writeFile(wb, filename);
};

/**
 * Export rebar weight data to CSV
 */
export const exportRebarToCSV = (rebarWeight, modelName = 'BIM Model') => {
  const lines = [];
  
  // Header
  lines.push('Anton Zolotarov BIM AI Platform - Structural Takeoff');
  lines.push('');
  lines.push(`Model,${modelName}`);
  lines.push(`Category,${rebarWeight.category}`);
  lines.push(`Total Weight (kg),${rebarWeight.totalWeightKg?.toLocaleString() || 0}`);
  lines.push(`Total Length (m),${rebarWeight.totalLengthM?.toLocaleString() || 0}`);
  lines.push(`Elements with Data,${rebarWeight.elementsWithData} of ${rebarWeight.elementCount}`);
  lines.push(`Generated,${new Date(rebarWeight.timestamp).toLocaleString()}`);
  lines.push('');
  
  // Per-level breakdown
  if (rebarWeight.byLevel && rebarWeight.byLevel.length > 0) {
    lines.push('Level Breakdown');
    lines.push('Level,Weight (kg),Length (m),Element Count');
    rebarWeight.byLevel.forEach(level => {
      lines.push(`${level.level},${level.weightKg?.toLocaleString() || 0},${level.lengthM?.toLocaleString() || 0},${level.count}`);
    });
  }
  
  // Warnings
  if (rebarWeight.warnings && rebarWeight.warnings.length > 0) {
    lines.push('');
    lines.push('Notes');
    rebarWeight.warnings.forEach(w => lines.push(w));
  }
  
  const csvContent = lines.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const filename = generateFilename('Rebar_Weight', 'csv');
  
  downloadBlob(blob, filename);
};

/**
 * Export rebar weight data to Excel with minimal branding
 */
export const exportRebarToExcel = (rebarWeight, modelName = 'BIM Model') => {
  const wb = XLSX.utils.book_new();
  
  // Create data array for the sheet
  const data = [
    ['Anton Zolotarov BIM AI Platform - Structural Takeoff'],
    [],
    ['Model', modelName],
    ['Category', rebarWeight.category],
    ['Total Weight (kg)', rebarWeight.totalWeightKg || 0],
    ['Total Length (m)', rebarWeight.totalLengthM || 0],
    ['Elements with Data', `${rebarWeight.elementsWithData} of ${rebarWeight.elementCount}`],
    ['Generated', new Date(rebarWeight.timestamp).toLocaleString()],
    [],
  ];
  
  // Add per-level breakdown if available
  if (rebarWeight.byLevel && rebarWeight.byLevel.length > 0) {
    data.push(['Level Breakdown']);
    data.push(['Level', 'Weight (kg)', 'Length (m)', 'Element Count']);
    rebarWeight.byLevel.forEach(level => {
      data.push([level.level, level.weightKg || 0, level.lengthM || 0, level.count]);
    });
  }
  
  // Add warnings if any
  if (rebarWeight.warnings && rebarWeight.warnings.length > 0) {
    data.push([]);
    data.push(['Notes']);
    rebarWeight.warnings.forEach(w => data.push([w]));
  }
  
  const ws = XLSX.utils.aoa_to_sheet(data);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 30 },  // Column A
    { wch: 15 },  // Column B
    { wch: 15 },  // Column C
    { wch: 15 },  // Column D
  ];
  
  // Apply minimal styling
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }, // Merge title row
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Rebar Takeoff');
  
  const filename = generateFilename('Rebar_Weight', 'xlsx');
  XLSX.writeFile(wb, filename);
};

/**
 * Helper to download blob as file
 */
const downloadBlob = (blob, filename) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};
